import { useEffect, useRef } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useRealtimeStore } from '../stores/realtimeStore'

const WS_URL = import.meta.env.VITE_WS_URL || 'wss://geocontexte-api.lxsrv.fr/ws'
const PING_INTERVAL = 30000
const RECONNECT_DELAY = 5000

export function useWebSocket(siteId: string | null) {
  const wsRef = useRef<WebSocket | null>(null)
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  const token = useAuthStore((s) => s.token)
  const setConnected = useRealtimeStore((s) => s.setConnected)
  const updatePosition = useRealtimeStore((s) => s.updatePosition)
  const updateBattery = useRealtimeStore((s) => s.updateBattery)
  const updateTopology = useRealtimeStore((s) => s.updateTopology)
  const clearPositions = useRealtimeStore((s) => s.clearPositions)

  useEffect(() => {
    mountedRef.current = true

    if (!siteId || !token) {
      setConnected(false)
      return
    }

    const connect = () => {
      if (!mountedRef.current) return

      const ws = new WebSocket(`${WS_URL}/tracking/${siteId}?token=${token}`)
      wsRef.current = ws

      ws.onopen = () => {
        if (!mountedRef.current) return
        console.log('[WS] Connected to tracking')
        setConnected(true)
        // Start ping
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping')
          }
        }, PING_INTERVAL)
      }

      ws.onmessage = (event) => {
        if (!mountedRef.current) return
        if (event.data === 'pong') return

        try {
          const msg = JSON.parse(event.data)
          switch (msg.type) {
            case 'position_update':
              console.log('[WS] position_update:', msg.badge_code)
              updatePosition(msg.badge_code, {
                locationName: msg.location_name,
                floor: msg.floor,
                battery: msg.battery ?? 100,
                rssi: msg.rssi ?? 0,
                timestamp: Date.now(),
              })
              break
            case 'battery_update':
              console.log('[WS] battery_update:', msg.badge_code)
              updateBattery(msg.badge_code, msg.battery)
              break
            case 'topology_update':
              console.log('[WS] topology_update:', msg.nodes?.length, 'nodes')
              updateTopology(msg.nodes ?? [], msg.edges ?? [])
              break
          }
        } catch {
          // Ignore malformed messages
        }
      }

      ws.onclose = (e) => {
        if (!mountedRef.current) return
        console.log('[WS] Disconnected, code:', e.code)
        setConnected(false)
        cleanup()
        // Auto-reconnect
        reconnectRef.current = setTimeout(connect, RECONNECT_DELAY)
      }

      ws.onerror = () => {
        console.error('[WS] Connection error')
      }
    }

    const cleanup = () => {
      if (pingRef.current) {
        clearInterval(pingRef.current)
        pingRef.current = null
      }
    }

    connect()

    return () => {
      mountedRef.current = false
      cleanup()
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current)
        reconnectRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
      setConnected(false)
      clearPositions()
    }
  }, [siteId, token, setConnected, updatePosition, updateBattery, updateTopology, clearPositions])
}
